import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { PlatformSpecificErrorBoundary } from "@/features/ErrorHandling/components/PlatformSpecificErrorBoundary";
import { sortSeriesBookingsByDate } from "@/features/RecurringRequests/recurrence";
import { Button } from "@whoosh/design-system/src/components/controls";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { SavedRequestRow } from "./SavedRequestRow";
import { SavedRequestSeriesQuery } from "./__generated__/SavedRequestSeriesQuery.graphql";

type Props = { bookingId: string; onClose: () => void };

export const SavedRequestSeries = ({ bookingId, onClose }: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<SavedRequestSeriesQuery>(query);
  useEffect(() => {
    loadQuery({ bookingId }, { fetchPolicy: "network-only" });
  }, [bookingId, loadQuery]);

  return (
    <PlatformSpecificErrorBoundary
      fallback={(_eventId, _error, resetError) => (
        <VStack gap={3}>
          <Text variant="mainBaseMedium">{t`Unable to load this request series.`}</Text>
          <Button
            accessibilityLabel={t`Retry loading request series`}
            variant="secondary"
            onPress={() => {
              loadQuery({ bookingId }, { fetchPolicy: "network-only" });
              resetError();
            }}
          >{t`Try again`}</Button>
        </VStack>
      )}
    >
      <Suspense fallback={<LoadingSpinner />}>
        {queryRef ? (
          <SavedRequestSeriesContent queryRef={queryRef} onClose={onClose} />
        ) : (
          <LoadingSpinner />
        )}
      </Suspense>
    </PlatformSpecificErrorBoundary>
  );
};

const SavedRequestSeriesContent = ({
  queryRef,
  onClose,
}: {
  queryRef: PreloadedQuery<SavedRequestSeriesQuery>;
  onClose: () => void;
}) => {
  const data = usePreloadedQuery(query, queryRef);
  const series =
    data.node?.__typename === "StandardBooking"
      ? data.node.bookingSeries
      : null;
  const bookings = sortSeriesBookingsByDate(
    series?.bookings?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) ?? []
  );

  return (
    <VStack gap={2} testID="recurring-request-saved-series">
      <Text variant="mainBaseSemiBold">{t`Requests`}</Text>
      {bookings.length ? (
        bookings.map((booking) => (
          <SavedRequestRow
            key={booking.id}
            bookingRef={booking}
            onClose={onClose}
          />
        ))
      ) : (
        <Text variant="mainBaseMedium">{t`No requests remain in this series.`}</Text>
      )}
    </VStack>
  );
};

const query = graphql`
  query SavedRequestSeriesQuery($bookingId: ID!) {
    node(id: $bookingId) {
      __typename
      ... on StandardBooking {
        bookingSeries {
          bookings(
            first: 366
            orderBy: [{ field: DATE_TIME, direction: DESC }]
          ) {
            edges {
              node {
                id
                startSlot {
                  agenda {
                    date
                  }
                }
                ...SavedRequestRow_booking
              }
            }
          }
        }
      }
    }
  }
`;
